import { ComponentGroup, HeatingSystem } from '@/util/modbus';
import { metricNameForMeasurementTypes } from './shared';

export type PufferspeicherLabel = 'VL' | 'RL' | 'M' | 'O';
export type PufferspeicherLabelPos = 'firstLabel' | 'secondLabel' | 'thirdLabel' | 'fourthLabel';

const DEFAULT_PUFFERSPEICHER_LABELS: PufferspeicherLabel[] = ['VL', 'RL'];
const ALL_PUFFERSPEICHER_LABELS: PufferspeicherLabel[] = ['VL', 'RL', 'M', 'O'];

export function isPufferspeicher(groupName: string): boolean {
  return groupName.toLowerCase().includes('pufferspeicher');
}

export function pufferShortLabelFromMeasurementType(measurementType?: string | null): PufferspeicherLabel | null {
  if (!measurementType) return null;
  const normalized = measurementType.toLowerCase();

  if (normalized.includes('mitte')) return 'M';
  if (normalized.includes('oben')) return 'O';

  // Vorlauf / Einlass variants
  if (normalized.includes('vorlauf') || normalized.includes('einlass') || normalized.includes('eintritt')) {
    return 'VL';
  }

  // Rücklauf / Auslass variants
  if (normalized.includes('rücklauf') || normalized.includes('auslass') || normalized.includes('austritt')) {
    return 'RL';
  }

  return null;
}

export function labelPosForPufferspeicherLabel(label: PufferspeicherLabel): PufferspeicherLabelPos {
  switch (label) {
    case 'VL':
      return 'firstLabel';
    case 'RL':
      return 'secondLabel';
    case 'M':
      return 'thirdLabel';
    case 'O':
      return 'fourthLabel';
    default:
      return 'firstLabel';
  }
}

export function getNeededPufferspeicherGroupNames(components: ComponentGroup[]): Set<string> {
  return new Set(components.filter((c) => isPufferspeicher(c.groupName)).map((c) => c.groupName));
}

export function initPresentByGroupName(groupNames: Set<string>): Record<string, Set<PufferspeicherLabel>> {
  const presentByGroupName: Record<string, Set<PufferspeicherLabel>> = {};
  for (const groupName of groupNames) {
    presentByGroupName[groupName] = new Set<PufferspeicherLabel>();
  }
  return presentByGroupName;
}

export function addPresentPufferLabels(
  presentByGroupName: Record<string, Set<PufferspeicherLabel>>,
  neededGroupNames: Set<string>,
  heatingSystems: HeatingSystem[],
): void {
  for (const hs of heatingSystems) {
    for (const group of hs.heatingSystemMeasurementGroups) {
      if (!neededGroupNames.has(group.groupName)) continue;

      const present = presentByGroupName[group.groupName];
      for (const measurement of group.heatingSystemMeasurements) {
        const label = pufferShortLabelFromMeasurementType(measurement.measurementType);
        if (label) {
          present.add(label);
        }
      }
    }
  }
}
// This function resolves the labels for a Pufferspeicher group, ensuring a consistent order and handling incomplete/mixed cases.
export function resolveOrderedPufferspeicherLabels(present: Set<PufferspeicherLabel>): PufferspeicherLabel[] {
  const hasAllFour = ALL_PUFFERSPEICHER_LABELS.every((label) => present.has(label));
  if (hasAllFour) return ALL_PUFFERSPEICHER_LABELS;

  if (present.has('VL') && present.has('RL')) return ['VL', 'RL'];
  if (present.has('M') && present.has('O')) return ['M', 'O'];

  // Fallback for mixed/incomplete cases where we do not have a clean pair.
  const picked = ALL_PUFFERSPEICHER_LABELS.filter((l) => present.has(l)).slice(0, 2);
  return picked.length === 2 ? picked : DEFAULT_PUFFERSPEICHER_LABELS;
}

export function findPufferspeicherGroupByLabel(
  groups: HeatingSystem['heatingSystemMeasurementGroups'],
  label: PufferspeicherLabel,
): HeatingSystem['heatingSystemMeasurementGroups'][number] | null {
  return (
    groups.find((group) =>
      group.heatingSystemMeasurements?.some((m) => pufferShortLabelFromMeasurementType(m.measurementType) === label),
    ) ?? null
  );
}

export function metricNameForPufferspeicherLabel(
  measurements: Array<{ measurementType?: string | null; metricName?: string | null }> | null | undefined,
  label: PufferspeicherLabel,
): string | null {
  if (!measurements || !Array.isArray(measurements)) return null;

  const byLabel: Record<PufferspeicherLabel, string[]> = {
    VL: ['vorlauf', 'einlasstemperatur', 'eintrittstemperatur'],
    RL: ['rücklauf', 'auslasstemperatur', 'austrittstemperatur'],
    M: ['mitte'],
    O: ['oben'],
  };

  return metricNameForMeasurementTypes(measurements, byLabel[label]);
}
